/**
 * Интерфейс данных для редактирования линии профиля
 */
export interface LineEditData {
    isRectangle: boolean;
    lineLength: number;
    lineLength2: number;
    lineAngle: number;
    lineAngleSign: 1 | -1;
    lineLengthMin: number;
    lineLengthMax: number;
    lineLength2Min: number;
    lineLength2Max: number;
    lineAngleMin: number;
    lineAngleMax: number;
    restrictTrapeze: boolean;
}

/**
 * Результат валидации линии профиля
 */
export interface ValidationResult {
    isValid: boolean;
    errors: Record<string, string>;
}

/**
 * Конфигурация валидатора линии профиля
 */
export interface ProfileLineValidatorConfig {
    minLength: number;
    maxLength: number;
    minAngle?: number;
    maxAngle: number;
    stepAngle?: number;
    canEditLineAngle: boolean;
}

/**
 * Валидатор для линий профиля
 * Отвечает за проверку корректности данных линии и генерацию сообщений об ошибках
 */
export class ProfileLineValidator {
    constructor(private config: ProfileLineValidatorConfig) {
    }

    /**
     * Валидация данных линии профиля
     * @param lineEditData - данные линии для валидации
     * @returns результат валидации с флагом валидности и объектом ошибок
     */
    public validate(lineEditData: Partial<LineEditData>): ValidationResult {
        const errors: Record<string, string> = {};
        let isValid = true;

        // Валидация минимальной длины первой полки
        if (!this.isVariableDefined(lineEditData.lineLengthMin) && lineEditData.lineLength < this.config.minLength) {
            isValid = false;

            errors['lineLength'] = this.getLineLengthMessage(lineEditData);
        }

        // Валидация минимальной длины второй полки
        if (!this.isVariableDefined(lineEditData.lineLength2Min) && lineEditData.lineLength2 < this.config.minLength) {
            isValid = false;

            errors['lineLength2'] = this.getLineLength2Message(lineEditData);
        }

        // Валидация максимального ограничения угла
        if (this.isVariableDefined(lineEditData.lineAngleMax)
            && (lineEditData.lineAngleMax > this.config.maxAngle || lineEditData.lineAngleMax < 0)) {
            isValid = false;

            errors['lineAngleMax'] = this.getLineAngleLimitsMessage();
        }

        // Валидация минимального ограничения угла
        if (this.isVariableDefined(lineEditData.lineAngleMin)
            && (lineEditData.lineAngleMin < 0 || lineEditData.lineAngleMin > this.config.maxAngle)) {
            isValid = false;

            errors['lineAngleMin'] = this.getLineAngleLimitsMessage();
        }

        // Валидация минимального ограничения длины первой полки
        if (this.isVariableDefined(lineEditData.lineLengthMin) && lineEditData.lineLengthMin < this.config.minLength) {
            isValid = false;

            errors['lineLengthMin'] = this.getLineLengthMinErrorMessage(lineEditData.lineLengthMax);
        }

        // Валидация минимального ограничения длины второй полки
        if (this.isVariableDefined(lineEditData.lineLength2Min) && lineEditData.lineLength2Min < this.config.minLength) {
            isValid = false;

            errors['lineLength2Min'] = this.getLineLengthMinErrorMessage(lineEditData.lineLength2Max);
        }

        // Валидация согласованности ограничений длины первой полки
        if (
            this.isVariableDefined(lineEditData.lineLengthMin)
            && this.isVariableDefined(lineEditData.lineLengthMax)
            && lineEditData.lineLengthMin > lineEditData.lineLengthMax
        ) {
            isValid = false;

            const errorMessage = 'Минимальное ограничение длины не может быть больше максимального';
            errors['lineLengthMin'] = errorMessage;
            errors['lineLengthMax'] = errorMessage;
        }

        // Валидация согласованности ограничений длины второй полки
        if (
            this.isVariableDefined(lineEditData.lineLength2Min)
            && this.isVariableDefined(lineEditData.lineLength2Max)
            && lineEditData.lineLength2Min > lineEditData.lineLength2Max
        ) {
            isValid = false;

            const errorMessage = 'Минимальное ограничение длины не может быть больше максимального';
            errors['lineLength2Min'] = errorMessage;
            errors['lineLength2Max'] = errorMessage;
        }

        // Валидация максимального ограничения длины первой полки
        if (this.isVariableDefined(lineEditData.lineLengthMax) && lineEditData.lineLengthMax < this.config.minLength) {
            isValid = false;

            errors['lineLengthMax'] = this.getLineLengthMaxErrorMessage(lineEditData.lineLengthMin);
        }

        // Валидация максимального ограничения длины второй полки
        if (this.isVariableDefined(lineEditData.lineLength2Max) && lineEditData.lineLength2Max < this.config.minLength) {
            isValid = false;

            errors['lineLength2Max'] = this.getLineLengthMaxErrorMessage(lineEditData.lineLength2Min);
        }

        // Валидация длины первой полки относительно минимального ограничения
        if (this.isVariableDefined(lineEditData.lineLengthMin) && lineEditData.lineLength < lineEditData.lineLengthMin) {
            isValid = false;

            errors['lineLength'] = this.getLineLengthMessage(lineEditData);
        }

        // Валидация длины первой полки относительно максимального ограничения
        if (lineEditData.lineLengthMax > 0 && lineEditData.lineLength > lineEditData.lineLengthMax) {
            isValid = false;

            errors['lineLength'] = this.getLineLengthMessage(lineEditData);
        }

        // Валидация длины второй полки относительно минимального ограничения
        if (this.isVariableDefined(lineEditData.lineLength2Min) && lineEditData.lineLength2 < lineEditData.lineLength2Min) {
            isValid = false;

            errors['lineLength2'] = this.getLineLength2Message(lineEditData);
        }

        // Валидация длины второй полки относительно максимального ограничения
        if (lineEditData.lineLength2Max > 0 && lineEditData.lineLength2 > lineEditData.lineLength2Max) {
            isValid = false;

            errors['lineLength2'] = this.getLineLength2Message(lineEditData);
        }

        // Валидация угла относительно минимального ограничения
        if (this.config.canEditLineAngle
            && this.isVariableDefined(lineEditData.lineAngleMin)
            && lineEditData.lineAngle < lineEditData.lineAngleMin) {
            isValid = false;

            errors['lineAngle'] = this.getLineAngleMessage(lineEditData);
        }

        // Валидация угла относительно максимального ограничения
        if (this.config.canEditLineAngle
            && ((this.isVariableDefined(lineEditData.lineAngleMax) && lineEditData.lineAngle > lineEditData.lineAngleMax)
                || (!lineEditData.lineAngleMax && lineEditData.lineAngle > this.config.maxAngle))) {
            isValid = false;

            errors['lineAngle'] = this.getLineAngleMessage(lineEditData);
        }

        // Валидация угла относительно минимального ограничения с учетом исключения для 0°
        if (this.config.canEditLineAngle
            && this.isVariableDefined(this.config.minAngle)
            && lineEditData.lineAngle !== 0
            && !this.isVariableDefined(lineEditData.lineAngleMin)
            && lineEditData.lineAngle < this.config.minAngle) {
            isValid = false;

            errors['lineAngle'] = this.getLineAngleMessage(lineEditData);
        }

        // Валидация кратности угла шагу
        if (this.config.canEditLineAngle
            && this.isVariableDefined(this.config.stepAngle)
            && this.config.stepAngle > 0
            && !this.isVariableDefined(lineEditData.lineAngleMax)
            && lineEditData.lineAngle !== 0
            && (lineEditData.lineAngle % this.config.stepAngle !== 0)) {
            isValid = false;

            errors['lineAngle'] = `Угол должен быть кратен ${this.config.stepAngle}°`;
        }

        return { isValid, errors };
    }

    /**
     * Обновление конфигурации валидатора
     */
    public updateConfig(config: Partial<ProfileLineValidatorConfig>): void {
        this.config = { ...this.config, ...config };
    }

    /**
     * Проверка, определена ли переменная
     */
    private isVariableDefined(variable: number): boolean {
        return variable !== null && variable !== undefined;
    }

    /**
     * Генерация сообщения об ошибке для длины первой полки
     */
    private getLineLengthMessage(lineEditData: Partial<LineEditData>): string {
        const min = lineEditData.lineLengthMin ?? this.config.minLength;
        const max = lineEditData.lineLengthMax ?? this.config.maxLength;
        return `Длина полки должна быть не менее ${min} и не более ${max} мм`;
    }

    /**
     * Генерация сообщения об ошибке для длины второй полки
     */
    private getLineLength2Message(lineEditData: Partial<LineEditData>): string {
        const min = lineEditData.lineLength2Min ?? this.config.minLength;
        const max = lineEditData.lineLength2Max ?? this.config.maxLength;
        return `Длина полки должна быть не менее ${min} и не более ${max} мм`;
    }

    /**
     * Генерация сообщения об ошибке для угла
     */
    private getLineAngleMessage(lineEditData: Partial<LineEditData>): string {
        const min = lineEditData.lineAngleMin ?? this.config.minAngle;
        const max = lineEditData.lineAngleMax ?? this.config.maxAngle;

        // Если minAngle > 0, добавляем информацию об исключении для 0°
        if (min > 0) {
            return `Угол должен быть равен 0° или находиться в диапазоне от ${min}° до ${max}°`;
        }

        return `Угол должен быть не менее ${min}° и не более ${max}°`;
    }

    /**
     * Генерация сообщения об ошибке для ограничений угла
     */
    private getLineAngleLimitsMessage(): string {
        return `Ограничения угла должны быть в диапазоне от 0 до ${this.config.maxAngle} градусов`;
    }

    /**
     * Генерация сообщения об ошибке для минимального ограничения длины
     */
    private getLineLengthMinErrorMessage(maxValue: number): string {
        const max = (maxValue && maxValue > this.config.minLength) ? maxValue : this.config.maxLength;
        return `Ограничение длины должны быть в диапазоне от ${this.config.minLength} до ${max}`;
    }

    /**
     * Генерация сообщения об ошибке для максимального ограничения длины
     */
    private getLineLengthMaxErrorMessage(minValue: number): string {
        const min = (minValue && minValue > this.config.minLength) ? minValue : this.config.minLength;
        return `Ограничение длины должны быть в диапазоне от ${min} до ${this.config.maxLength}`;
    }
}
